import { z } from "zod";
import { ClientsWorkOverviewColumn } from "@common/types/views/clientsWorkOverviewView";
import type {
    BoardLaneDef,
    BoardPlacement,
} from "@design-system/Board/Board.types";
import type { ClientsWorkOverviewWorkRow } from "../clientsWorkOverviewViewConfig";
import {
    getWorkPersonGroupIds,
    getWorkPersonGroups,
    getWorkPersonLaneWithDetails,
    isWorkPersonRole,
    WORK_PERSON_ROLES,
    type WorkPersonRole,
} from "../workPersonGroups";
import {
    getWorkBoardColumns,
    getWorkRows,
    type WorkBoardColumnBy,
    type WorkBoardColumnsArgs,
} from "./workBoardColumns";

const WORK_BOARD_LANE_BYS = [
    "client",
    "template",
    "stage",
    "due_date",
    ...WORK_PERSON_ROLES,
] as const;

/** What swimlanes can show: a column grouping, or one engagement team role. */
export type WorkBoardLaneBy = (typeof WORK_BOARD_LANE_BYS)[number];

/** A stored lane choice, parsed; anything the page doesn't offer is null. */
export const workBoardLaneBySchema = z
    .enum(WORK_BOARD_LANE_BYS)
    .nullable()
    .catch(null);

const LANE_LABELS: Record<WorkBoardLaneBy, string> = {
    client: "Client",
    template: "Template",
    stage: "Stage",
    due_date: "Work end",
    preparer: "Preparer",
    reviewer: "Reviewer",
    manager: "Manager",
};

/** The gear's swimlane choices after None, in its order. */
export const WORK_BOARD_LANE_OPTIONS = WORK_BOARD_LANE_BYS.map((value) => ({
    value,
    label: LANE_LABELS[value],
}));

/** The lanes the board draws: none when the stored choice would repeat the columns. */
export const getWorkBoardLaneBy = ({
    storedLaneBy,
    columnBy,
}: {
    storedLaneBy: WorkBoardLaneBy | null;
    columnBy: WorkBoardColumnBy;
}): WorkBoardLaneBy | null => (storedLaneBy === columnBy ? null : storedLaneBy);

const AXIS_FIELDS: Record<WorkBoardLaneBy, ClientsWorkOverviewColumn | null> = {
    stage: ClientsWorkOverviewColumn.CLOSE_STAGE,
    client: ClientsWorkOverviewColumn.WORKSPACE_NAME,
    template: null,
    due_date: null,
    preparer: null,
    reviewer: null,
    manager: null,
};

/**
 * The card fields the columns and lanes already show, so cards leave them
 * off. Work end buckets don't show the date and a role's lane shows one of
 * the engagement team's roles, so those fields stay.
 */
export const getWorkBoardAxisFields = ({
    columnBy,
    laneBy,
}: {
    columnBy: WorkBoardColumnBy;
    laneBy: WorkBoardLaneBy | null;
}): ReadonlySet<string> =>
    new Set(
        [AXIS_FIELDS[columnBy], laneBy && AXIS_FIELDS[laneBy]].filter(
            (field): field is ClientsWorkOverviewColumn => !!field,
        ),
    );

interface WorkBoardLanes {
    lanes: BoardLaneDef[];
    getLaneIds: (row: ClientsWorkOverviewWorkRow) => string[];
}

/** How person lanes are titled: by name alone, or with their details. */
export type WorkPersonLaneTitle = "name" | "details";

/**
 * One lane per person in the role on the page, by name, and "No {role}" last
 * when it has work or empty columns show, each titled as `personLaneTitle`
 * says.
 */
const getPersonLanes = ({
    role,
    rows,
    isEmptyColumnsShown,
    personLaneTitle,
}: {
    role: WorkPersonRole;
    rows: readonly ClientsWorkOverviewWorkRow[];
    isEmptyColumnsShown: boolean;
    personLaneTitle: WorkPersonLaneTitle;
}): WorkBoardLanes => ({
    lanes: getWorkPersonGroups({ role, rows })
        .filter((group) => isEmptyColumnsShown || group.rows.length > 0)
        .map((group) =>
            personLaneTitle === "details"
                ? getWorkPersonLaneWithDetails(group)
                : { id: group.id, title: group.title },
        ),
    getLaneIds: (row) => getWorkPersonGroupIds({ role, row }),
});

/** Lanes by a column grouping are that grouping's columns: same ids, titles, order and empty rules. */
const getGroupingLanes = (args: WorkBoardColumnsArgs): WorkBoardLanes => {
    const { columns, getPlacements } = getWorkBoardColumns(args);
    return {
        lanes: columns.map(({ id, title, icon }) => ({ id, title, icon })),
        getLaneIds: (row) => getPlacements(row).map(({ columnId }) => columnId),
    };
};

export interface WorkBoardModel {
    columns: ReturnType<typeof getWorkBoardColumns>["columns"];
    /** Undefined without swimlanes. */
    lanes: BoardLaneDef[] | undefined;
    getPlacements: (row: ClientsWorkOverviewWorkRow) => BoardPlacement[];
}

interface WorkBoardModelArgs extends WorkBoardColumnsArgs {
    /** The lanes the board draws, from `getWorkBoardLaneBy`. */
    laneBy: ReturnType<typeof getWorkBoardLaneBy>;
    /** Defaults to "name". */
    personLaneTitle?: WorkPersonLaneTitle;
}

/**
 * The board's columns and swimlanes, and each application's places: its
 * column in each of its lanes. A page that gives no lanes draws none, so it
 * reads like the board without swimlanes.
 */
export const getWorkBoardModel = ({
    laneBy,
    personLaneTitle = "name",
    ...columnArgs
}: WorkBoardModelArgs): WorkBoardModel => {
    const board = getWorkBoardColumns(columnArgs);
    if (laneBy === null) {
        return { ...board, lanes: undefined };
    }
    const { lanes, getLaneIds } = isWorkPersonRole(laneBy)
        ? getPersonLanes({
              role: laneBy,
              rows: getWorkRows(columnArgs.workspaceRows),
              isEmptyColumnsShown: columnArgs.isEmptyColumnsShown,
              personLaneTitle,
          })
        : getGroupingLanes({ ...columnArgs, columnBy: laneBy });
    if (lanes.length === 0) {
        return { ...board, lanes: undefined };
    }
    return {
        columns: board.columns,
        lanes,
        getPlacements: (row) =>
            board
                .getPlacements(row)
                .flatMap(({ columnId }) =>
                    getLaneIds(row).map((laneId) => ({ columnId, laneId })),
                ),
    };
};
